import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';

import { APP_NAME, POOL_NAME } from '@/constants';
import { useModal } from '@/context/ModalContext';
import { refreshTokenConfig } from '@/oidcConfig';
import { fetchAuditableElements } from '@/slices/auditableElementsSlice';
import { clearOnLogOut, refreshSign, refreshSignIn } from '@/slices/authSlice';
import { AppDispatch, RootState } from '@/store';

const useShouldRefresh = () => {
  const dispatch: AppDispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const { refreshToken, username, isOidcClient } = auth;

  const { needRefresh } = refreshTokenConfig(auth);
  const { updateModalContent, closeModal } = useModal();
  const { signinSilent } = useAuth();

  useEffect(() => {
    const handleRefresh = async () => {
      if (!needRefresh) return;
      if (auth?.status?.message === 'loading') return;

      try {
        if (isOidcClient) {
          const user = await signinSilent();
          if (user) {
            dispatch(
              refreshSignIn({
                accessToken: user.access_token,
                idToken: user.id_token,
                refreshToken: user.refresh_token,
                expiresIn: user.expires_at
              })
            );
            dispatch(
              fetchAuditableElements({
                applicationName: APP_NAME
              })
            );
          } else {
            dispatch(clearOnLogOut());
          }
        } else {
          await dispatch(
            refreshSign({
              refreshToken,
              username: username || '',
              poolName: POOL_NAME
            })
          );
        }
      } catch (error: unknown) {
        console.error('❌ Error refreshing token', error);
        if (error instanceof Error) {
          updateModalContent(null, {
            modalTitle: 'Refresh Token',
            modalButtons: [
              {
                label: 'OK',
                onClick: () => {
                  closeModal();
                },
                variant: 'okBlue'
              }
            ],
            alignButtons: 'center',
            contentTitle: error?.message || 'Error'
          });
        }
      }
    };

    handleRefresh();
  }, [dispatch, auth?.status?.message, needRefresh]);
};

export default useShouldRefresh;
